import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Fieldset } from 'primereact/fieldset';
import { InputText } from 'primereact/inputtext';
import { Toolbar } from 'primereact/toolbar';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import customerService from './customer-service';
import { InputSwitch } from 'primereact/inputswitch';
import { useTranslation } from 'react-i18next';
import BreadcrumbComponent from '../../../components/Breadcrumb';

interface DropdownItem {
    referenceName: string;
    referenceCode: string;
}
const currentDate = new Date();
const newDate = new Date(currentDate.getTime() + 8 * 60 * 60 * 1000);

const CustomerEdit: React.FC = () => {

    const { t, i18n } = useTranslation('common');
    const [formData, setFormData] = useState<any>({
        registrationDate: currentDate,
    });
    const [errors, setErrors] = useState<any>({});
    const [clientState, setClientState] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const customer = location.state?.customer;
    const isEditMode = !!customer;

    const [stateItems, setStateItems] = useState<DropdownItem[]>([]);
    const [countryItems, setCountryItems] = useState<DropdownItem[]>([]);
    const [districtItems, setDistrictItems] = useState<DropdownItem[]>([]);
    const [departmentItems, setDepartmentItems] = useState<DropdownItem[]>([]);
    const [customerTypeItems, setCustomerTypeItems] = useState<DropdownItem[]>([]);
    const [postcodeItems, setPostcodeItems] = useState<DropdownItem[]>([]);
    const dropdownStateItems = useMemo(() => stateItems, [stateItems]);
    const dropdownCountryItems = useMemo(() => countryItems, [countryItems]);
    const dropdownDistrictItems = useMemo(() => districtItems, [districtItems]);
    const dropdownDepartmentItems = useMemo(() => departmentItems, [departmentItems]);
    const dropdownCustomerTypeItems = useMemo(() => customerTypeItems, [customerTypeItems]);
    const dropdownPostcodeItems = useMemo(() => postcodeItems, [postcodeItems]);

    useEffect(() => {
        const fetchClientState = async () => {
            try {
                const clientStateItem = await customerService.getClientState();
                setClientState(clientStateItem);

                const stateData = await customerService.getStateDropdown('MYS');
                setStateItems(stateData);

                const countryData = await customerService.getCountryDropdown();
                setCountryItems(countryData);

                const deptData = await customerService.getDeptDropdown();
                setDepartmentItems(deptData);

                const custTypeData = await customerService.getCustTypeDropdown('CUSTOMER_TYPE', 'ADP');
                setCustomerTypeItems(custTypeData);

                if (isEditMode) {
                    setFormData(customer)

                    if (customer) {
                        const districtData = await customerService.getDistrictDropdown(customer.stateCode);
                        setDistrictItems(districtData);

                        const postcodeData = await customerService.getPostcodeDropdown(customer.stateCode);
                        setPostcodeItems(postcodeData);
                    }
                }

            } catch (error) {
                console.error('Error fetching client state:', error);
            }
        };

        fetchClientState();
    }, []);

    const validate = () => {
        const newErrors: {
            customerCode?: any;
            fullName?: any;
            mnemonic?: any;
            departmentCode?: any;
            customerType?: any;
            countryCode?: any;
            stateCode?: any;
        } = {};

        if (!formData.customerCode) {
            newErrors.customerCode = t('customer-code-is-required');
        }
        if (!formData.fullName) {
            newErrors.fullName = t('customer-name-is-required');
        }
        if (!formData.mnemonic) {
            newErrors.mnemonic = t('mnemonic-is-required');
        }
        if (!formData.departmentCode) {
            newErrors.departmentCode = t('department-is-required');
        }
        if (!formData.customerType) {
            newErrors.customerType = t('customer-type-is-required');
        }
        if (!formData.countryCode) {
            newErrors.countryCode = t('country-is-required');
        }
        if (!formData.stateCode) {
            newErrors.stateCode = t('state-is-required');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e: any) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value.toUpperCase()
        });
    };

    const handleDropdownChange = (e: any) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value
        });
    };

    const handleStateDropdownChange = async (e: any) => {
        const stateCode = e.value

        if (stateCode) {
            try {
                const districtData = await customerService.getDistrictDropdown(stateCode);
                setDistrictItems(districtData);

                const postcodeData = await customerService.getPostcodeDropdown(stateCode);
                setPostcodeItems(postcodeData);
            } catch (error) {
                console.error('Error fetching district data:', error);
            }
        }

        setFormData({
            ...formData,
            stateCode: e.value
        });

    };

    const handleSave = async (event: React.FormEvent) => {
        event.preventDefault();

        if (validate()) {

            setLoading(true); // Set loading to true when submitting the form

            const newDate = new Date(currentDate.getTime() + 8 * 60 * 60 * 1000);

            formData.recordStatus = formData.recordStatus == true ? '1' : '0';

            if (isEditMode) {
                formData.modifiedBy = clientState.userId;
                formData.modifiedDate = newDate.toISOString();
            } else {
                formData.insertBy = clientState.userId;
                formData.insertDate = newDate.toISOString();
                formData.modifiedBy = clientState.userId;
                formData.modifiedDate = newDate.toISOString();
                formData.facilityCode = clientState.facilityCode;
                formData.systemTenant = 0
            }

            if (isEditMode) {
                try {

                    const response = await customerService.update(formData.customerCode, formData);
                    await new Promise((resolve) => setTimeout(resolve, 2000));
                    alert(t('success-submit-form'));
                    navigate(`/admin/customer`);
                } catch (error) {
                    console.error("Error with Axios:", error);
                    alert(t('fail-submit-form'));
                } finally {
                    setLoading(false);
                }
            } else {
                try {
                    const response = await customerService.create(formData);
                    await new Promise((resolve) => setTimeout(resolve, 2000));
                    alert(t('success-submit-form'));

                    navigate(`/admin/customer`);
                } catch (error) {
                    console.error("Error with Axios:", error);
                    alert(t('fail-submit-form'));
                } finally {
                    setLoading(false);
                }
            }
        }
    }

    const toolbarTemplate = () => {

        const handleBack = () => {
            navigate(`/admin/customer`);
        }

        return (
            <>
                <Button label={t('save')} icon="pi pi-check" style={{ marginRight: '.5em' }} onClick={handleSave} disabled={loading}>

                </Button>
                {/* <Button label="Delete" icon="pi pi-trash" style={{ marginRight: '.5em' }} /> */}
                <Button label={t('back')} icon="pi pi-times" style={{ marginRight: '.5em' }} onClick={handleBack}>

                </Button>

            </>
        );
    };

    const formatDateToDisplay = (dateString: string | null | undefined) => {
        if (!dateString) return null; // Handle null or undefined
        return new Date(dateString); // Ensure it's a valid Date object
    };

    return (
        <div className='grid'>
            <div className='col-12'>
                <div className='card-title'>
                    <h5>{t('cust-form')}</h5>
                    <BreadcrumbComponent />
                </div>
                <div className='form-title'>
                    <Fieldset legend={t('cust-info')}>
                        <div className="p-fluid form-grid grid">

                            <div className="field col-12 md:col-6">
                                <label>{t('registration-date')}</label>
                                <Calendar
                                    value={formatDateToDisplay(formData.registrationDate)}
                                    dateFormat="dd MM yy"
                                    showIcon
                                    showTime
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>{t('cust-code')}</label>
                                <InputText
                                    id="customerCode"
                                    type="text"
                                    value={formData.customerCode}
                                    onChange={handleInputChange}
                                />
                                {errors.customerCode && <small className="p-error">{errors.customerCode}</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>{t('customer-name')}</label>
                                <InputText
                                    id="fullName"
                                    type="text"
                                    value={formData.fullName}
                                    onChange={handleInputChange}
                                />
                                {errors.fullName && <small className="p-error">{errors.fullName}</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>{t('mnemonic')}</label>
                                <InputText
                                    id="mnemonic"
                                    type="text"
                                    value={formData.mnemonic}
                                    onChange={handleInputChange}
                                />
                                {errors.mnemonic && <small className="p-error">{errors.mnemonic}</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>{t('department')}</label>
                                <Dropdown
                                    id="departmentCode"
                                    value={formData.departmentCode}
                                    onChange={handleDropdownChange}
                                    options={dropdownDepartmentItems}
                                    optionLabel="fullDesc"
                                    placeholder={t('select-one')}
                                    optionValue="departmentCode"
                                >
                                </Dropdown>
                                {errors.departmentCode && <small className="p-error">{errors.departmentCode}</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>{t('cust-type')}</label>
                                <Dropdown
                                    id="customerType"
                                    value={formData.customerType}
                                    onChange={handleDropdownChange}
                                    options={dropdownCustomerTypeItems}
                                    optionLabel="fullDesc"
                                    placeholder={t('select-one')}
                                    optionValue="referenceCode"
                                >
                                </Dropdown>
                                {errors.customerType && <small className="p-error">{errors.customerType}</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <h6>{t('record-status')}</h6>
                                <InputSwitch
                                    checked={formData.recordStatus}
                                    onChange={(e) => setFormData({ ...formData, recordStatus: e.value })}
                                />
                            </div>

                        </div>
                    </Fieldset>
                    <div>
                        <br />
                    </div>
                    <Fieldset legend={t('contact-info')} toggleable>
                        <div className="p-fluid form-grid grid">
                            <div className="field col-12">
                                <label>{t('address')}</label>
                                <InputText
                                    id="address1"
                                    type="text"
                                    value={formData.address1}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>{t('country')}</label>
                                <Dropdown
                                    id="countryCode"
                                    value={formData.countryCode}
                                    onChange={handleDropdownChange}
                                    options={dropdownCountryItems}
                                    optionLabel="fullDesc"
                                    placeholder={t('select-one')}
                                    optionValue="countryCode"
                                >
                                </Dropdown>
                                {errors.countryCode && <small className="p-error">{errors.countryCode}</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>{t('state')}</label>
                                <Dropdown
                                    id="stateCode"
                                    value={formData.stateCode}
                                    onChange={(e) => handleStateDropdownChange(e)}
                                    options={dropdownStateItems}
                                    optionLabel="fullDesc"
                                    placeholder={t('select-one')}
                                    optionValue="stateCode"
                                >
                                </Dropdown>
                                {errors.stateCode && <small className="p-error">{errors.stateCode}</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>{t('postcode')}</label>
                                <Dropdown
                                    id="postcode"
                                    value={formData.postcode}
                                    onChange={handleDropdownChange}
                                    options={dropdownPostcodeItems}
                                    optionLabel="postcode"
                                    placeholder={t('select-one')}
                                    optionValue="postcode"
                                >
                                </Dropdown>
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>{t('district')}</label>
                                <Dropdown
                                    id="districtCode"
                                    value={formData.districtCode}
                                    onChange={handleDropdownChange}
                                    options={dropdownDistrictItems}
                                    optionLabel="fullDesc"
                                    placeholder={t('select-one')}
                                    optionValue="districtCode"
                                >
                                </Dropdown>
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>{t('city')}</label>
                                <InputText
                                    id="city"
                                    type="text"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                />
                            </div>


                            <div className="field col-12 md:col-6">
                                <label>{t('tel-no')}</label>
                                <InputText
                                    id="telephoneNo"
                                    type="text"
                                    value={formData.telephoneNo}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>{t('fax-no')}</label>
                                <InputText
                                    id="faxNo"
                                    type="text"
                                    value={formData.faxNo}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>{t('email')}</label>
                                <InputText
                                    id="email"
                                    type="text"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                    </Fieldset>
                    <div>
                        <br />
                    </div>

                    <Toolbar end={toolbarTemplate}></Toolbar>
                </div>
            </div>

        </div>
    );
};

export default CustomerEdit;